import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ParticleBackground from '../components/ParticleBackground';

// Import images - REMOVED unused dashboardPreview
import aiInterface from '../assets/images/platform/ai-interface.png';
import dataFlow from '../assets/images/platform/data-flow.png';
import securityDashboard from '../assets/images/security/security-dashboard.png';

// CountUp Animation Component
const CountUp = ({ end, duration = 2000, suffix = '', decimals = 0, prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    // FIXED: Store ref in variable to avoid ref mutation warning
    const currentRef = countRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]); // FIXED: Added isVisible dependency

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end * Math.pow(10, decimals)) / Math.pow(10, decimals));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [isVisible, end, duration, decimals]);

  return (
    <span ref={countRef}>
      {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </span>
  );
};

function Home() {
  return (
    <>
      <section className="hero">
        <ParticleBackground />
        <div className="canvas-blur-overlay"></div>
        <div className="hero-content container">
          <div className="hero-label" data-aos="fade-up" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <span className="pulse-dot"></span>
            Enterprise AI Solutions
          </div>
          <h1 data-aos="fade-up" data-aos-delay="100" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Transform Your Scattered Data<br />
            Into a <span className="gradient-text animated-gradient">Private AI Assistant</span>
          </h1>
          <p data-aos="fade-up" data-aos-delay="200" style={{ fontFamily: "'Inter', sans-serif" }}>
            We help enterprises convert documents, CRM systems, WhatsApp conversations, 
            PDFs, SOPs, product catalogs, and HR policies into a unified, secure AI assistant 
            that delivers instant answers — available 24/7 with military-grade security.
          </p>
          <div className="hero-buttons" data-aos="fade-up" data-aos-delay="300">
            <Link to="/contact" className="cta-button glow-effect" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span>Schedule Free Consultation</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <Link to="/features" className="button-secondary glass-effect" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span>See How It Works</span>
            </Link>
          </div>
          
          <div className="floating-stats" data-aos="fade-up" data-aos-delay="400">
            <div className="stat-card glass-effect">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="stat-value" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>10x</div>
              <div className="stat-label" style={{ fontFamily: "'Inter', sans-serif" }}>Faster Performance</div>
            </div>
            <div className="stat-card glass-effect">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="stat-value" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>99.9%</div>
              <div className="stat-label" style={{ fontFamily: "'Inter', sans-serif" }}>Security Rate</div>
            </div>
            <div className="stat-card glass-effect">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="stat-value" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>24/7</div>
              <div className="stat-label" style={{ fontFamily: "'Inter', sans-serif" }}>Always Available</div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-ticker">
        <div className="ticker-content" style={{ fontFamily: "'Inter', sans-serif" }}>
          <div className="ticker-item"><strong>99.9%</strong> Uptime Guarantee</div>
          <div className="ticker-item"><strong>10x</strong> Faster Information Retrieval</div>
          <div className="ticker-item"><strong>100%</strong> Private and Secure</div>
          <div className="ticker-item"><strong>24/7</strong> Always Available</div>
          <div className="ticker-item"><strong>Enterprise</strong> Grade Infrastructure</div>
          <div className="ticker-item"><strong>ISO 27001</strong> Certified</div>
        </div>
      </section>

    {/* IMAGE SECTION 1: Platform Overview with Background Image */}
<section 
  className="enterprise-overview" 
  style={{
    backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(10, 10, 10, 0.9) 100%), url(${aiInterface})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Animated gradient overlay */}
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 50%, rgba(168, 85, 247, 0.15) 0%, transparent 60%)',
    animation: 'pulseGradient 10s ease-in-out infinite',
    pointerEvents: 'none'
  }}></div>

  <div className="container" style={{ position: 'relative', zIndex: 1 }}>
    <div className="overview-content">
      <div className="overview-text" data-aos="fade-right">
        <span className="section-label" style={{ 
          background: 'rgba(168, 85, 247, 0.2)',
          color: '#a855f7',
          padding: '0.5rem 1.5rem',
          borderRadius: '50px',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          fontFamily: "'Space Grotesk', sans-serif"
        }}>
          Enterprise AI Platform
        </span>
        <h2 style={{ 
          color: 'white', 
          marginTop: '1.5rem',
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: '300'
        }}>
          Unified Intelligence Across Your Organization
        </h2>
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.85)',
          fontFamily: "'Inter', sans-serif"
        }}>
          Our AI implementation platform consolidates disparate data sources into a single, 
          intelligent knowledge system. Built for enterprise scale, security, and compliance 
          with seamless integration into your existing infrastructure.
        </p>
        <Link to="/features" className="text-link" style={{ 
          color: '#a855f7',
          fontFamily: "'Space Grotesk', sans-serif"
        }}>
          Explore Platform Capabilities
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
      <div className="overview-stats" data-aos="fade-left">
        {/* Glass morphism stat cards with CountUp */}
        <div className="overview-stat-item" style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="stat-large" style={{
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            <CountUp end={500} duration={2500} suffix="+" />
          </div>
          <div className="stat-description" style={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Inter', sans-serif"
          }}>
            Enterprise Deployments
          </div>
        </div>
        <div className="overview-stat-item" style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="stat-large" style={{
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            <CountUp end={2.5} decimals={1} duration={2500} suffix="M+" />
          </div>
          <div className="stat-description" style={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Inter', sans-serif"
          }}>
            Daily Queries Processed
          </div>
        </div>
        <div className="overview-stat-item" style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="stat-large" style={{
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            <CountUp end={99.9} decimals={1} duration={2500} suffix="%" />
          </div>
          <div className="stat-description" style={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Inter', sans-serif"
          }}>
            Accuracy Rate
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* IMAGE SECTION 2: Enhanced Data Integration */}
<section className="premium-benefits">
  <div className="container">
    <div className="section-header-centered">
      <span className="section-label" data-aos="fade-up" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Data Integration</span>
      <h2 className="section-title" data-aos="fade-up" data-aos-delay="100" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '300' }}>
        Unified Data Architecture for Modern Enterprises
      </h2>
      <p className="section-description" data-aos="fade-up" data-aos-delay="200" style={{ fontFamily: "'Inter', sans-serif" }}>
        Connect 200+ data sources through API-first integration. Real-time synchronization with enterprise-grade security and compliance built-in.
      </p>
      
      {/* IMAGE: Data Flow Diagram with Enhanced Styling */}
      <div data-aos="fade-up" data-aos-delay="300" style={{ 
        margin: '4rem 0',
        position: 'relative',
        padding: '3rem',
        background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.03) 0%, rgba(236, 72, 153, 0.05) 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(168, 85, 247, 0.1)'
      }}>
        <img 
          src={dataFlow} 
          alt="Enterprise Data Integration Architecture" 
          style={{
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'block',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(168, 85, 247, 0.15), 0 0 0 1px rgba(168, 85, 247, 0.1)',
            transition: 'transform 0.3s ease'
          }}
        />
        {/* Integration badges */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginTop: '2rem',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '50px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            fontSize: '0.9rem',
            fontWeight: '500',
            fontFamily: "'Inter', sans-serif"
          }}>
            ⚡ Real-Time Sync
          </div>
          <div style={{
            background: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '50px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            fontSize: '0.9rem',
            fontWeight: '500',
            fontFamily: "'Inter', sans-serif"
          }}>
            🔒 End-to-End Encryption
          </div>
          <div style={{
            background: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '50px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            fontSize: '0.9rem',
            fontWeight: '500',
            fontFamily: "'Inter', sans-serif"
          }}>
            📊 200+ Connectors
          </div>
        </div>
      </div>
    </div>

    <div className="premium-benefit-row" data-aos="fade-up">
      <div className="premium-benefit-number" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>01</div>
      <div className="premium-benefit-content">
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Intelligent Document Processing</h2>
        <p style={{ fontFamily: "'Inter', sans-serif" }}>AI-powered extraction from PDFs, contracts, reports, and unstructured documents with 99.2% accuracy. Automated classification, metadata tagging, and semantic indexing for instant retrieval across 50+ file formats including legacy systems.</p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>OCR Processing</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>NLP Analysis</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Auto-Classification</span>
        </div>
      </div>
    </div>

    <div className="premium-divider"></div>

    <div className="premium-benefit-row reverse" data-aos="fade-up">
      <div className="premium-benefit-number" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>02</div>
      <div className="premium-benefit-content">
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>360° Customer Data Platform</h2>
        <p style={{ fontFamily: "'Inter', sans-serif" }}>Real-time bidirectional sync with Salesforce, HubSpot, Zendesk, and custom CRMs. Unified customer profiles with interaction history, predictive analytics, and automated enrichment. Support for REST APIs, webhooks, and event-driven architectures.</p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Live Sync</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Data Enrichment</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>API Gateway</span>
        </div>
      </div>
    </div>

    <div className="premium-divider"></div>

    <div className="premium-benefit-row" data-aos="fade-up">
      <div className="premium-benefit-number" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>03</div>
      <div className="premium-benefit-content">
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Enterprise Data Fabric & Governance</h2>
        <p style={{ fontFamily: "'Inter', sans-serif" }}>Cloud-native integration with PostgreSQL, MongoDB, Oracle, SAP HANA, and data lakes. Automated data lineage tracking, quality monitoring, and compliance enforcement (GDPR, CCPA, SOC 2). Built-in data cataloging and metadata management.</p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Data Lineage</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Quality Gates</span>
          <span style={{
            background: 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            padding: '0.4rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontFamily: "'Inter', sans-serif"
          }}>Compliance Auto</span>
        </div>
      </div>
    </div>
  </div>
</section>

      <section className="enterprise-overview">
        <div className="container">
          <div className="section-header-centered">
            <span className="section-label" data-aos="fade-up" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Industry Solutions</span>
            <h2 className="section-title" data-aos="fade-up" data-aos-delay="100" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '300' }}>
              Tailored for Your Industry
            </h2>
          </div>

          <div className="overview-content">
            <div className="overview-text" data-aos="fade-right">
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Financial Services & Healthcare</h2>
              <p style={{ fontFamily: "'Inter', sans-serif" }}>
                Regulatory compliance, risk analysis, client advisory, trading documentation, clinical protocols, 
                patient information, research data, and compliance documentation with HIPAA and SOC 2 compliance.
              </p>
            </div>
            <div className="overview-text" data-aos="fade-left">
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Manufacturing & Technology</h2>
              <p style={{ fontFamily: "'Inter', sans-serif" }}>
                Technical specifications, supply chain data, quality assurance, safety procedures, product documentation, 
                engineering specs, codebase knowledge, and customer support integration.
              </p>
            </div>
          </div>

          <div className="premium-divider"></div>

          <div className="overview-content">
            <div className="overview-text" data-aos="fade-right">
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Legal & Professional Services</h2>
              <p style={{ fontFamily: "'Inter', sans-serif" }}>
                Case law, contracts, precedents, regulatory frameworks, client documentation, deliverables, 
                methodologies, proposals, and institutional expertise management.
              </p>
            </div>
            <div className="overview-text" data-aos="fade-left">
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Custom Enterprise Solutions</h2>
              <p style={{ fontFamily: "'Inter', sans-serif" }}>
                Tailored AI implementations for unique business requirements, custom integrations, 
                specialized compliance needs, and industry-specific workflows across all sectors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE SECTION 3: Security Dashboard */}
      <section className="enterprise-security">
        <div className="container">
          <div className="security-content">
            <div className="security-text" data-aos="fade-right">
              <span className="section-label" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Enterprise Security</span>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '300' }}>Built for Compliance and Control</h2>
              <div className="security-features">
                <div className="security-feature-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <div>
                    <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>End-to-End Encryption</h4>
                    <p style={{ fontFamily: "'Inter', sans-serif" }}>AES-256 encryption for data at rest and in transit with zero-knowledge architecture</p>
                  </div>
                </div>
                <div className="security-feature-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <div>
                    <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>On-Premise Deployment</h4>
                    <p style={{ fontFamily: "'Inter', sans-serif" }}>Full data sovereignty with private cloud, on-premise, or hybrid deployment options</p>
                  </div>
                </div>
                <div className="security-feature-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <div>
                    <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Compliance Certifications</h4>
                    <p style={{ fontFamily: "'Inter', sans-serif" }}>GDPR, SOC 2 Type II, ISO 27001, HIPAA compliant with regular third-party audits</p>
                  </div>
                </div>
                <div className="security-feature-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <polyline points="17 11 19 13 23 9" />
                  </svg>
                  <div>
                    <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '400' }}>Access Controls</h4>
                    <p style={{ fontFamily: "'Inter', sans-serif" }}>Role-based permissions, SSO, MFA, and comprehensive audit logs with activity monitoring</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="security-visual" data-aos="fade-left">
              {/* IMAGE: Security Dashboard */}
              <img 
                src={securityDashboard} 
                alt="Enterprise Security Dashboard" 
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(168, 85, 247, 0.25)',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content container" data-aos="fade-up">
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Ready to Transform Your Business Intelligence?</h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }}>Schedule a consultation with our enterprise AI specialists to discuss your specific requirements and deployment strategy.</p>
          <Link to="/contact" className="button-large glow-effect" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Schedule Consultation
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
        <div className="cta-particles"></div>
      </section>
    </>
  );
}

export default Home;
